import React from "react"; // No need to import { Component }
import { Card, Divider, Grid, Header, Icon, Table } from "semantic-ui-react";
import "../../node_modules/react-vis/dist/style.css";
import ChartUtils from "../charts/ChartUtils";
import "../css/home.css"; // Ensure you add the CSS classes here
import Utils from "../util/Utils";
import { isMobile } from "react-device-detect";

const renderTableYearHeaders = (dataSource, yearDataKey) => {
  return Object.keys(dataSource).map((index) => {
    const dataItem = dataSource[index];
    const year = dataItem?.[yearDataKey]?.year;

    if (year) {
      const headerLabel = year.substring(0, 4); // Assuming year is like "YYYY-MM-DD"
      return <Table.HeaderCell key={index}>{headerLabel}</Table.HeaderCell>;
    }

    return null;
  });
};

const renderDataCells = (
  dataSource,
  dataKey,
  format_function,
  valuePath,
  params
) => {
  return Object.keys(dataSource).map((index) => {
    // Safely access the specific value using the dataKey and valuePath
    const cellData = dataSource[index]?.[dataKey]?.[valuePath];

    const cellElement = Utils.TableCell(
      cellData, // Pass the actual value
      format_function,
      ...params // Spread the params array (e.g., [10, 1])
    );

    return React.cloneElement(cellElement, { key: index });
  });
};

// Define the configuration for the rows in the table body for Net Income growth
const netIncomeGrowthRowsConfig = [
  {
    label: "Net Income", // Label for the first cell in the row
    dataKey: "Net Income", // Key used to access data in incomeStmtValues[index]
    valuePath: "value", // Path under dataKey to get the specific value
    format: Utils.format_currency_short, // Formatting function
    params: [10, 1], // Additional params for Utils.TableCell (adjust if needed based on Utils implementation)
    trendChartKey: "Net Income", // Key for getXyDataFromValues for the tiny chart
    trendChartDataKey: "Value", // Data key for the tiny chart
  },
  {
    label: "YoY Growth",
    dataKey: "Net Income", // Use "Net Income" data for growth calculation
    valuePath: "yoyGrowth", // Use yoyGrowth property
    format: Utils.format_percent,
    params: [0.1, 1], // Additional params for Utils.TableCell (adjust if needed)
    trendChartKey: "Net Income", // Trend chart is based on Net Income data itself
    trendChartDataKey: "Growth", // Data key for the tiny chart
  },
  // Add other relevant rows here if needed
];

// Functional Component
const NetIncomeGrowthCheck = (props) => {
  const incomeYValues = props.stockValues.incomeStmtValues.slice(
    isMobile ? -3 : -5
  );

  return (
    <>
      <Divider horizontal>
        <Header as="h4">
          <Icon size="large" name="chart line" />
          Is Net Income positive and growing consistently ?
        </Header>
      </Divider>
      <br />

      <Grid stackable centered>
        <Grid.Row columns={{ mobile: 1, tablet: 2, computer: 3 }}>
          <Grid.Column width={5}>
            <div className="chart-container">
              {ChartUtils.renderGrowthCard(
                "Net Income", // Title for the growth card
                ChartUtils.getXyDataFromValues(
                  props.stockValues.incomeStmtValues, // Use income statement data
                  "Net Income" // Use Net Income data key
                ),
                Utils.format_number // Formatting for the card value
              )}
            </div>
          </Grid.Column>

          <Grid.Column width={6}>
            <div style={{ padding: "0 0.5rem" }}>
              <Card fluid raised>
                <Card.Content>
                  <Card.Header textAlign="center">
                    Past Years Values
                    <Header.Subheader style={{ marginTop: "0.25em" }}>
                      (10 to 15% growth is ideal)
                    </Header.Subheader>
                  </Card.Header>
                  <Card.Description>
                    <Table
                      size="small"
                      unstackable
                      compact="very"
                      celled
                      className="table-margin-auto" // Use CSS class for margin
                    >
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Year</Table.HeaderCell>
                          {renderTableYearHeaders(incomeYValues, "Net Income")}
                          <Table.HeaderCell>Trend</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {netIncomeGrowthRowsConfig.map((rowConfig) => (
                          <Table.Row key={rowConfig.label}>
                            <Table.Cell>{rowConfig.label}</Table.Cell>
                            {renderDataCells(
                              incomeYValues, // Pass the full data object
                              rowConfig.dataKey, // Pass the data key (e.g., "Net Income")
                              rowConfig.format, // Pass the format function
                              rowConfig.valuePath, // Pass the path to the specific value (.value or .yoyGrowth)
                              rowConfig.params // Pass additional params for Utils.TableCell
                            )}
                            {ChartUtils.renderTinyChart(
                              ChartUtils.getXyDataFromValues(
                                incomeYValues,
                                rowConfig.trendChartKey // Use the trend chart key from config
                              ),
                              rowConfig.trendChartDataKey // Use the trend chart data key from config
                            )}
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </Card.Description>
                </Card.Content>
                <Card.Content extra>
                  Along with the sales/revenue, the profit (Net Income) of the
                  company should also grow because it is the profit that matters
                  at the end of the day. A company whose profit is stagnant or
                  decreasing should be avoided. However, For early stage
                  companies, it takes few years to become profitable.
                </Card.Content>
              </Card>
            </div>
          </Grid.Column>

          <Grid.Column width={5}>
            <div className="chart-container">
              {ChartUtils.renderObservations(
                props.title, // Assuming props.title is the stock ticker or name
                props.stockValues.netIncomeObservations // Use netIncomeObservations
              )}
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </>
  );
};

export default NetIncomeGrowthCheck;
